import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Loader2, Download, Sparkles, CalendarCheck, Home, Globe } from 'lucide-react';
import { ShareDemoPublic } from '../types';
import { getPublicShareDemo, recordShareDemoEvent } from '../services/supabase';
import BeforeAfterSlider from './BeforeAfterSlider';
import EmailCaptureModal from './EmailCaptureModal';
import { SHARE_DEMO_APPS, getShareDemoUrl } from '../shareDemoApps';

const REHUB_FOOTER = {
  name: 'ReHub Studio',
  homeUrl: 'https://hub.rehub.studio',
  logo: 'https://hub.rehub.studio/ReHub-Logo-White.png',
} as const;

interface ShareDemoPageProps {
  slug: string;
}

const getVisitorId = (): string => {
  const key = 'pc_share_visitor_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

const getSessionId = (): string => {
  const key = 'pc_share_session_id';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
};

export default function ShareDemoPage({ slug }: ShareDemoPageProps) {
  const [demo, setDemo] = useState<ShareDemoPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const viewTracked = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  useEffect(() => {
    (async () => {
      const data = await getPublicShareDemo(slug);
      if (!data || !data.processed_image_url) {
        setNotFound(true);
      } else {
        const canonicalUrl = getShareDemoUrl(data.workflow_type, slug);
        if (typeof window !== 'undefined' && window.location.origin !== new URL(canonicalUrl).origin) {
          window.location.replace(canonicalUrl);
          return;
        }
        setDemo(data);
        const brandName = data.workflow_type === 'hyperdusk'
          ? 'Hyper Dusk'
          : data.workflow_type === 'video'
          ? 'ReHub Studio'
          : data.workflow_type === 'staging'
          ? 'Virtual Staging Studio'
          : 'Photoclear';
        document.title = data.public_headline || `${brandName} Demo`;

        const setMeta = (property: string, content: string) => {
          let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
          if (!tag) {
            tag = document.createElement('meta');
            tag.setAttribute('property', property);
            document.head.appendChild(tag);
          }
          tag.content = content;
        };
        setMeta('og:title', data.public_headline || `${brandName} Demo`);
        setMeta('og:description', data.public_subheadline || '');
        setMeta('og:image', data.processed_image_url);
        const demoHasVideo = (data.workflow_type === 'hyperdusk' || data.workflow_type === 'video') && !!data.processed_video_url;
        setMeta('og:type', demoHasVideo ? 'video.other' : 'website');
        if (demoHasVideo && data.processed_video_url) {
          setMeta('og:video', data.processed_video_url);
        }
      }
      setLoading(false);
    })();
  }, [slug]);

  useEffect(() => {
    if (demo && !viewTracked.current) {
      viewTracked.current = true;
      recordShareDemoEvent(slug, 'view', {
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
      });
    }
  }, [demo, slug]);

  // Browsers block autoplay-with-sound, so the video starts muted. Unmute it
  // on the visitor's first interaction with the page (click / tap / key).
  useEffect(() => {
    if (!demo) return;
    const unmute = () => {
      const video = videoRef.current;
      if (video) {
        video.muted = false;
        video.volume = 1;
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
      remove();
    };
    const remove = () => {
      window.removeEventListener('pointerdown', unmute);
      window.removeEventListener('keydown', unmute);
      window.removeEventListener('touchstart', unmute);
    };
    window.addEventListener('pointerdown', unmute);
    window.addEventListener('keydown', unmute);
    window.addEventListener('touchstart', unmute);
    return remove;
  }, [demo]);

  const handleSliderInteraction = useCallback(() => {
    recordShareDemoEvent(slug, 'slider_interaction', {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
    });
  }, [slug]);

  const triggerDownload = useCallback(async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 200);
    } catch {
      window.open(url, '_blank');
    }

    recordShareDemoEvent(slug, 'download_complete', {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
    });
  }, [slug]);

  const downloadAsset = useCallback(() => {
    if (!demo) return;
    const useVideo = (demo.workflow_type === 'hyperdusk' || demo.workflow_type === 'video') && !!demo.processed_video_url;
    const url = useVideo ? demo.processed_video_url! : demo.processed_image_url;
    const filename = useVideo
      ? (demo.workflow_type === 'hyperdusk' ? 'hyperdusk-animation.mp4' : 'video.mp4')
      : demo.workflow_type === 'staging'
      ? 'staged-image.jpg'
      : 'photoclear-enhanced.jpg';
    triggerDownload(url, filename);
  }, [demo, triggerDownload]);

  const handleDownloadClick = useCallback(() => {
    if (!demo) return;
    recordShareDemoEvent(slug, 'download_click', {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
    });

    if (demo.download_mode === 'free') {
      downloadAsset();
    } else if (demo.download_mode === 'email_gate') {
      setShowEmailModal(true);
    }
  }, [demo, slug, downloadAsset]);

  const handleEmailSubmit = useCallback(async (data: { email: string; first_name?: string; agency_name?: string }) => {
    if (!demo) return;
    await recordShareDemoEvent(slug, 'email_capture', {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      email: data.email,
      first_name: data.first_name,
      agency_name: data.agency_name,
    });
    setShowEmailModal(false);
    downloadAsset();
  }, [demo, slug, downloadAsset]);

  const handleCtaClick = useCallback((type: string, url: string) => {
    recordShareDemoEvent(slug, 'cta_click', {
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      metadata: { cta_type: type },
    });
    window.open(url, '_blank');
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (notFound || !demo) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 p-6 text-center">
        <img
          src="/Photoclear Logo HR Trans.png"
          alt="Photoclear"
          className="h-10 w-auto mb-4 opacity-60"
        />
        <h1 className="text-2xl font-bold text-white">Demo not found</h1>
        <p className="text-slate-400 max-w-sm">
          This demo link may have expired or been removed.
        </p>
      </div>
    );
  }

  const showSignupCta = demo.cta_mode === 'signup' || demo.cta_mode === 'both';
  const showDemoCta = demo.cta_mode === 'demo' || demo.cta_mode === 'both';
  const isHyperDusk = demo.workflow_type === 'hyperdusk' && !!demo.processed_video_url;
  const isVideo = demo.workflow_type === 'video' && !!demo.processed_video_url;
  const isTour = demo.workflow_type === 'tour' && !!demo.tour_url;
  const isStaging = demo.workflow_type === 'staging';
  const hasVideo = isHyperDusk || isVideo;
  const showDownload = demo.download_mode !== 'disabled' && !isTour;
  const appsUsed = (demo.apps_used || [])
    .map((id) => SHARE_DEMO_APPS.find((a) => a.id === id))
    .filter((a): a is (typeof SHARE_DEMO_APPS)[number] => !!a);

  // When the page is already branded for a workflow, don't repeat that app in
  // "Created with" — e.g. a staging demo shouldn't show the VSS logo twice.
  const WORKFLOW_PRIMARY_APP: Partial<Record<ShareDemoPublic['workflow_type'], string>> = {
    photoclear: 'photoclear',
    hyperdusk: 'hyperdusk',
    staging: 'staging',
    tour: 'tours',
  };
  const creditApps = appsUsed.filter((a) => a.id !== WORKFLOW_PRIMARY_APP[demo.workflow_type]);

  // Workflow-aware branding
  const brand = isTour
    ? {
        name: '360 Tours',
        homeUrl: 'https://360tours.rehub.studio',
        signupUrl: demo.cta_signup_url,
        signupCta: 'Get Started',
        downloadCta: 'Download',
        accent: 'bg-sky-600 hover:bg-sky-500 shadow-sky-500/20',
        signupAccent: 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20',
        headerLogo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png',
        loaderColor: 'text-sky-400',
      }
    : isVideo
    ? {
        name: 'ReHub Studio',
        homeUrl: 'https://hub.rehub.studio',
        signupUrl: demo.cta_signup_url,
        signupCta: 'Get Started',
        downloadCta: 'Download Video',
        accent: 'bg-orange-600 hover:bg-orange-500 shadow-orange-500/20',
        signupAccent: 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20',
        headerLogo: '/ReHub Logo Transparent White Text .png',
        loaderColor: 'text-orange-500',
      }
    : isHyperDusk
    ? {
        name: 'Hyper Dusk',
        homeUrl: 'https://hyperdusk.rehub.studio',
        signupUrl: 'https://hyperdusk.rehub.studio/signup',
        signupCta: 'Try Hyper Dusk Free',
        downloadCta: 'Download Animation',
        accent: 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20',
        signupAccent: 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20',
        headerLogo: '/hyperdusk-logo-transparent.png',
        loaderColor: 'text-indigo-400',
      }
    : isStaging
    ? {
        name: 'Virtual Staging Studio',
        homeUrl: 'https://staging.rehub.studio',
        signupUrl: 'https://staging.rehub.studio/signup',
        signupCta: 'Try Virtual Staging Free',
        downloadCta: 'Download Staged Image',
        accent: 'bg-violet-600 hover:bg-violet-500 shadow-violet-500/20',
        signupAccent: 'bg-violet-600 hover:bg-violet-500 shadow-violet-500/20',
        headerLogo: 'https://staging.rehub.studio/virtual-staging-logo-transparent.png',
        loaderColor: 'text-violet-400',
      }
    : {
        name: 'Photoclear',
        homeUrl: 'https://photoclear.rehub.studio',
        signupUrl: demo.cta_signup_url,
        signupCta: 'Try Photoclear Free',
        downloadCta: 'Download Enhanced Image',
        accent: 'bg-orange-600 hover:bg-orange-500 shadow-orange-500/20',
        signupAccent: 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20',
        headerLogo: '/Photoclear Logo HR Trans.png',
        loaderColor: 'text-orange-500',
      };

  // For Hyper Dusk demos the configured signup URL stays admin-controllable,
  // but if the admin left the default Photoclear URL we override to Hyper Dusk.
  const signupHref = (isHyperDusk && /photoclear/.test(demo.cta_signup_url))
    || (isStaging && /photoclear/.test(demo.cta_signup_url))
    ? brand.signupUrl
    : demo.cta_signup_url;

  // Label the signup CTA after its destination. ReHub-hosted signups (the hub)
  // read "Try ReHub Free" even on a Photoclear-branded demo page.
  const signupLabel = !isHyperDusk && !isVideo && !isStaging && /hub\.rehub\.studio/.test(demo.cta_signup_url)
    ? 'Try ReHub Free'
    : brand.signupCta;

  return (
    <div className="min-h-screen bg-black" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      {/* Header with logo */}
      <header className="py-6 sm:py-8 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <a href={brand.homeUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={brand.headerLogo}
              alt={brand.name}
              className="h-14 sm:h-16 w-auto opacity-90 hover:opacity-100 transition-opacity"
            />
          </a>
          {demo.agency_logo_url ? (
            <img
              src={demo.agency_logo_url}
              alt={demo.agency_name || 'Client'}
              className="h-12 sm:h-16 w-auto max-w-[160px] sm:max-w-[220px] object-contain"
            />
          ) : demo.agency_name ? (
            <span className="text-lg sm:text-2xl text-slate-200 font-semibold tracking-wide">
              {demo.agency_name}
            </span>
          ) : null}
        </div>
      </header>

      {/* Main content */}
      <div className="px-4 sm:px-6 pb-10 sm:pb-16">
        <div className="w-full max-w-3xl mx-auto space-y-10">
          {/* Copy */}
          <div className="text-center space-y-3">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
              {demo.public_headline}
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto">
              {demo.public_subheadline}
            </p>
            {demo.source_label && (
              /^https?:\/\//i.test(demo.source_label.trim()) ? (
                <p className="text-sm">
                  <a
                    href={demo.source_label.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleCtaClick('source', demo.source_label!.trim())}
                    className="text-slate-400 hover:text-white underline underline-offset-2 transition-colors break-all"
                  >
                    {demo.source_label}
                  </a>
                </p>
              ) : (
                <p className="text-sm text-slate-500 italic">{demo.source_label}</p>
              )
            )}
          </div>

          {/* Before / After Slider (still day → night for Hyper Dusk).
              Skipped for video-only and 360-tour demos. */}
          {!isVideo && !isTour && (
            <BeforeAfterSlider
              beforeUrl={demo.original_image_url}
              afterUrl={demo.processed_image_url}
              onInteraction={handleSliderInteraction}
            />
          )}

          {/* Embedded 360 virtual tour */}
          {isTour && demo.tour_url && (
            <div className="space-y-3">
              <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-2xl shadow-black/40 aspect-video">
                <iframe
                  src={demo.tour_url}
                  title={demo.public_headline || '360 tour'}
                  className="w-full h-full"
                  allow="accelerometer; gyroscope; fullscreen; xr-spatial-tracking"
                  allowFullScreen
                />
              </div>
              <div className="text-center">
                <button
                  onClick={() => handleCtaClick('tour', demo.tour_url!)}
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white underline underline-offset-2 transition-colors"
                >
                  <Globe size={16} />
                  Open the full tour
                </button>
              </div>
            </div>
          )}

          {/* Cinematic video (Hyper Dusk time-lapse or an uploaded clip) */}
          {hasVideo && demo.processed_video_url && (
            <div className="space-y-3">
              <div className="text-center">
                <p className={`text-xs uppercase tracking-[0.2em] font-semibold ${isHyperDusk ? 'text-indigo-300' : 'text-slate-400'}`}>
                  {isHyperDusk ? 'Watch the time-lapse' : 'Watch the video'}
                </p>
              </div>
              <div className={`rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-2xl ${isHyperDusk ? 'shadow-indigo-900/20' : 'shadow-black/40'}`}>
                <video
                  ref={videoRef}
                  src={demo.processed_video_url}
                  className="w-full bg-black"
                  controls
                  muted
                  loop
                  playsInline
                  autoPlay
                  poster={demo.processed_image_url}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {showDownload && (
              <button
                onClick={handleDownloadClick}
                className={`inline-flex items-center gap-2.5 px-6 py-3.5 text-white font-semibold rounded-xl shadow-lg transition-colors text-sm ${brand.accent}`}
              >
                <Download size={18} />
                {brand.downloadCta}
              </button>
            )}

            {showSignupCta && (
              <button
                onClick={() => handleCtaClick('signup', signupHref)}
                className={`inline-flex items-center gap-2.5 px-6 py-3.5 text-white font-semibold rounded-xl shadow-lg transition-colors text-sm ${brand.signupAccent}`}
              >
                <Sparkles size={18} />
                {signupLabel}
              </button>
            )}

            {showDemoCta && (
              <button
                onClick={() => handleCtaClick('demo', demo.cta_demo_url)}
                className="inline-flex items-center gap-2.5 px-6 py-3.5 border border-slate-700 hover:border-slate-600 text-slate-300 hover:text-white font-semibold rounded-xl transition-colors text-sm"
              >
                <CalendarCheck size={18} />
                Book a 15-Minute Demo
              </button>
            )}

            {demo.listing_url && (
              <button
                onClick={() => handleCtaClick('listing', demo.listing_url!)}
                className="inline-flex items-center gap-2.5 px-6 py-3.5 border border-slate-700 hover:border-slate-600 text-slate-300 hover:text-white font-semibold rounded-xl transition-colors text-sm"
              >
                <Home size={18} />
                Portal Link
              </button>
            )}
          </div>

          {/* Other ReHub apps used alongside the primary workflow brand */}
          {creditApps.length > 0 && (
            <div className="pt-4 space-y-4">
              <p className="text-center text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Created with
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5">
                {creditApps.map((app) => (
                  <a
                    key={app.id}
                    href={app.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleCtaClick(`app:${app.id}`, app.url)}
                    className="group flex items-center"
                    title={app.name}
                  >
                    <img
                      src={app.logo}
                      alt={app.name}
                      style={{ height: `${(app.logoScale ?? 1) * 2.5}rem` }}
                      className="w-auto opacity-70 group-hover:opacity-100 transition-opacity"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer — service logo stays in the header; footer credits ReHub Studio */}
      {demo.branding_enabled && (
        <footer className="py-8 px-6 border-t border-slate-800/60 mt-4">
          <div className="max-w-3xl mx-auto flex items-center justify-center">
            <a href={REHUB_FOOTER.homeUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
              {!isVideo && (
                <img
                  src={REHUB_FOOTER.logo}
                  alt={REHUB_FOOTER.name}
                  className="h-7 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
                />
              )}
              <span className="text-sm text-slate-500 group-hover:text-slate-400 transition-colors whitespace-nowrap">
                Powered by <span className="font-semibold text-slate-300 group-hover:text-white">{REHUB_FOOTER.name}</span>
              </span>
            </a>
          </div>
        </footer>
      )}

      {/* Email capture modal */}
      {showEmailModal && (
        <EmailCaptureModal
          onSubmit={handleEmailSubmit}
          onClose={() => setShowEmailModal(false)}
          agencyHint={demo.agency_name}
        />
      )}
    </div>
  );
}
